import React, {
 PropsWithChildren, useEffect, useRef, useState,
} from 'react';
import { Link } from '../../../services/navigation/types';
import { NavigationService } from '../../../services';
import { NavigationItem, RocheNavigationItem } from './roche-navigation-item';

interface ICart {
  link: Link;
  count: number;
  maxCount: number;
}

interface INavigation {
  primary_navigation: NavigationItem[];
  secondary_navigation: NavigationItem[];
  global_search_link: Link;
  login_link: Link;
}

interface IRocheHeader extends PropsWithChildren {
  navigation: INavigation,
  cart: ICart;
  showNavigation?: boolean;
  isFixed?: boolean;
}

const initialMobileState = (
  typeof window === 'undefined' ? false : (
    window.innerWidth < 1200
  )
);

export const RocheHeader = ({
  showNavigation,
  navigation,
  cart,
  isFixed,
}: IRocheHeader) => {
  const ref = useRef<HTMLElement>(null);

  const [mobile, setMobile] = useState(initialMobileState);

  useEffect(() => {
    const header = ref.current;
    if (!header) return;

    const observer = new MutationObserver((entries) => {
      const { attributes } = entries.pop().target as HTMLElement;
      setMobile(!!attributes.getNamedItem('is-mobile'));
    });

    observer.observe(header, { attributes: true });

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [ref]);

  return <roche-navigation-header
      ref={ref}
      show-navigation={showNavigation}
      fixed={isFixed}
      global-search-link={NavigationService.getParsedNavigationLink(navigation?.global_search_link)}
      cart-link={
        NavigationService.shouldDisplayCartIconInHeader()
          ? NavigationService.getParsedNavigationLink(cart.link)
          : undefined
      }
      max-cart-count={cart.maxCount}
      cart-count={cart.count}>

      {showNavigation && (
        <>
          <roche-navigation slot="primary">
            <ul role="menubar">
              {navigation?.primary_navigation?.map((item, i) => (
                <li key={i} role="none">
                  {item.url && (
                    <RocheNavigationItem
                      url={item.url}
                      nested_links={item.nested_links}
                      text={item.text}
                      mobile={mobile} />
                    )}
                </li>
              ))}
            </ul>
          </roche-navigation>

          <roche-navigation slot="secondary">
              <ul role="menubar">
                {navigation?.secondary_navigation?.map((item, i) => (
                  <li key={i}>
                    {item.url && (
                      <RocheNavigationItem
                        url={item.url}
                        nested_links={item.nested_links}
                        text={item.text}
                        mobile={mobile} />
                      )}
                  </li>
                ))}
            </ul>
          </roche-navigation>

          <roche-navigation slot="tertiary">
            <ul role="menubar">
              {NavigationService.getParsedLink(navigation?.login_link) && (
                <li role="none">
                  <RocheNavigationItem
                    url={navigation?.login_link}
                    icon={'lock'}
                    iconSize={mobile ? '32px' : '20px'}
                  />
                </li>
              )}
              {NavigationService.getParsedLink(navigation?.global_search_link) && (
                <li role="none">
                  <RocheNavigationItem
                    url={navigation?.global_search_link}
                    icon={mobile ? 'search-mobile' : 'search'}
                    iconSize={mobile ? '32px' : '20px'}
                  />
                </li>
              )}

              {NavigationService.shouldDisplayCartIconInHeader()
                && NavigationService.getParsedLink(cart.link)
                && (
                  <li role="none">
                    <RocheNavigationItem
                      url={cart.link}
                    >
                      <roche-cart-icon
                        count={cart.count}
                        max-count={cart.maxCount}
                        icon={mobile ? 'cart-mobile' : 'cart-outline'}
                        height={mobile ? '32px' : '20px'} />
                    </RocheNavigationItem>
                  </li>
                )
              }
            </ul>
          </roche-navigation>
        </>
      )}
    </roche-navigation-header>;
};
